import { createApi } from "@reduxjs/toolkit/query/react";
import { VITE_API_V2_URL } from "codes";
import { axiosBaseQuery } from "helpers/axiosbaseQuery";
import { defaultAgreementStatus } from "helpers/agreementStatusHelpers";
import { isPositiveAmount } from "helpers/isPositiveAmount";
import type { SalesOpportunity } from "../types";
import type { CreditCommiteeStatuses } from "./api-credit-committee.d";
import type { OpportunityPicklistData } from "components/Map/Sections/Post-CC/types";
import {
  BUYER_CREDIT_REPORT_GREEN,
  CREDIT_REPORT_IN_PROGRESS,
  SUPPLIER_CREDIT_REPORT_GREEN,
} from "constants/opportunityCreditReportStatus";
import type { OpportunityStageTimer } from "helpers/opportunityDaysHelpers";

export type OpportunityPipelineItem = SalesOpportunity & {
  supplierInternalName?: string | null;
  buyerInternalName?: string | null;
  setupFeesPaidAt?: string | null;
  buyerApprovedLimit?: number | null;
  mapApprovedLimit?: number | null;
  timer?: OpportunityStageTimer | null;
};

const getProgress = (item: CreditCommiteeStatuses): number => {
  //key indicators

  const termsheetSigned =
    item?.termsheet_signed_date && item?.termsheet_signed_date !== "" ? 20 : 0;
  // eslint-disable-next-line no-extra-boolean-cast
  const welcomeCallDone = Boolean(item?.welcome_call_status) ? 15 : 0;
  let credit = 0;
  if (
    SUPPLIER_CREDIT_REPORT_GREEN.has(
      item?.credit_report_supplier_status ?? "",
    ) &&
    BUYER_CREDIT_REPORT_GREEN.has(item?.credit_report_buyer_status ?? "")
  ) {
    credit = 15;
  } else if (
    SUPPLIER_CREDIT_REPORT_GREEN.has(
      item?.credit_report_supplier_status ?? "",
    ) ||
    BUYER_CREDIT_REPORT_GREEN.has(item?.credit_report_buyer_status ?? "")
  ) {
    credit = 10;
  } else if (
    CREDIT_REPORT_IN_PROGRESS.has(item?.credit_report_supplier_status ?? "") ||
    CREDIT_REPORT_IN_PROGRESS.has(item?.credit_report_buyer_status ?? "")
  ) {
    credit = 7.5;
  }
  const creditReportCollected = credit;
  const onboardingDocumentsCollected = item?.onboarding_documents?.status
    ? 15
    : 0;
  // eslint-disable-next-line no-extra-boolean-cast
  const screeningDone = Boolean(item?.screening?.status) ? 15 : 0;
  const riskCallDone =
    Boolean(item?.buyer_risk_call?.status) && Boolean(item?.supplier_risk_call)
      ? 20
      : 0;

  return (
    termsheetSigned +
    welcomeCallDone +
    creditReportCollected +
    onboardingDocumentsCollected +
    screeningDone +
    riskCallDone
  );
};

const transformCCResponse = (
  data: CreditCommiteeStatuses[],
): OpportunityPipelineItem[] => {
  return data.map((item) => {
    const buyerApprovedLimitRaw = (item as any)?.buyer_approved_limit ?? null;
    const mapApprovedLimitRaw =
      (item as any)?.map_approved_limit ??
      (item as any)?.insurer_approved_limit ??
      null;
    const insurerApprovedCurrency =
      (item as any)?.insurer_approved_currency ??
      (item as any)?.insurer_currency ??
      null;
    const initialApprovedLimit =
      (item as any)?.credit_limit_approved_amount ??
      (item as any)?.approved_limit_amount ??
      (item as any)?.approved_limit ??
      null;
    const buyerApprovedLimit = isPositiveAmount(buyerApprovedLimitRaw)
      ? Number(buyerApprovedLimitRaw)
      : null;
    const mapApprovedLimit = isPositiveAmount(mapApprovedLimitRaw)
      ? Number(mapApprovedLimitRaw)
      : null;

    return {
      salesPartner:
        (item as any)?.sales_partner_name ??
        (item as any)?.sales_partner ??
        (item as any)?.salesPartner ??
        "",
      // New fields for requested / initially approved CL amounts
      requestedCL: (item as any)?.credit_limit_requested_amount ?? null,
      initialApprovedLimit,
      buyerApprovedLimit,
      mapApprovedLimit,
      insurerApprovedLimit: mapApprovedLimit,
      insurerApprovedCurrency,
      insurerCurrency: insurerApprovedCurrency,
      cofaceRating:
        (item as any)?.coface_rating ?? (item as any)?.coface_rateing ?? null,
      salesOpportunityId: {
        name: item?.opportunity_name,
        link: item?.opportunity_link,
      },
      etaMonth: item?.eta,
      trade_history: item?.trade_history,
      // trade_documents_risk: item?.trade_documents_risk,
      trade_documents_legal: item?.trade_documents_legal,
      supplier_financials: item?.supplier_financials,
      past_trade_docs_received: item?.past_trade_docs_received,
      trade_doc_operation_reviewed: item?.trade_doc_operation_reviewed === true,
      sales_comments:
        (item as any)?.sales_comments ?? (item as any)?.salesComments ?? "",
      funding_comments:
        (item as any)?.funding_comments ?? (item as any)?.fundingComments ?? "",
      risk_comments:
        (item as any)?.risk_comments ?? (item as any)?.riskComments ?? "",
      priority: item?.priority,
      opportunity_id: item?.opportunity_id,
      supplier: item?.supplier,
      supplierInternalName: item?.supplier_internal_name ?? null,
      isVirtualAccountNeeded: (item as any)?.is_virtual_account_needed === true,
      isVirtualAccountSetup: (item as any)?.is_virtual_account_setup === true,
      supplierCountry: item?.supplier_country ?? null,
      buyer: item?.buyer,
      buyerInternalName: item?.buyer_internal_name ?? null,
      buyerCountry: item?.buyer_country ?? null,
      insurer: item?.insurer,
      creditLimitProvidedByInsurer:
        (item as any)?.credit_limit_amount ??
        (item as any)?.credit_limit ??
        (item as any)?.creditLimitProvidedByInsurer,
      termsheetSignedDate: item?.termsheet_signed_date,
      welcomeCallDate: item?.welcome_call_date,
      setup_fees_paid: item?.setup_fees_paid,
      setupFeesPaidAt: item?.setup_fees_paid_at ?? null,
      welcomeCallStatus: item?.welcome_call_status,
      creditReportRequestedDate: item?.credit_report_requested_date,
      credit_report_supplier_status: item?.credit_report_supplier_status,
      credit_report_buyer_status: item?.credit_report_buyer_status,
      // onboardingDocuments: item?.onboarding_documents,
      screening: item?.screening,
      supplierRiskCall: item?.supplier_risk_call,
      supplierRiskCallDate: item?.supplier_risk_call_date,
      buyerRiskCall: item?.buyer_risk_call,
      buyerRiskCallDate: item?.buyer_call_date ?? "",
      buyerFinancials: item?.buyer_financials,
      salesOwner: item?.sales_owner,
      progress: getProgress(item),
      product: item?.product,
      invoiceCurrency: item?.invoice_currency,
      insuranceCurrency: item?.insurance_currency,
      fundingComments: item?.funding_comments,
      salesComments: item?.sales_comments,
      riskComments: item?.risk_comments,
      agreement: item?.agreement ?? defaultAgreementStatus(item?.product),
      mapApproved: (item as any)?.map_approved === true,
      mapApprovedAt: (item as any)?.map_approved_at ?? null,
      timer: ((item as { timer?: OpportunityStageTimer | null }).timer ??
        null) as OpportunityStageTimer | null,
      mapStatus: (item as any)?.map_status,
      creditLimitCurrency:
        (item as any)?.credit_limit_currency ?? item?.invoice_currency,
      status: item?.status, // Add status field to preserve the status object with id and name
      tradeOps: (item as any)?.trade_ops ?? null,
    };
  });
};
export const creditCommittee = createApi({
  reducerPath: "creditCommittee",
  tagTypes: ["STATUS"],
  baseQuery: axiosBaseQuery({
    baseUrl: `${VITE_API_V2_URL}`,
  }),
  endpoints: (builder) => ({
    getCCOnboardingStatuses: builder.query<
      OpportunityPipelineItem[],
      { status?: string }
    >({
      query: ({ status = "2-oip" }) => ({
        url: "/opportunity-pipeline",
        method: "get",
        params: { status },
      }),
      transformResponse: (response) => transformCCResponse(response?.data),
      providesTags: ["STATUS"],
      keepUnusedDataFor: 0,
      forceRefetch() {
        return true;
      },
    }),

    UpdateOppoPrDetail: builder.mutation<
      { success: boolean },
      { opporId: string; data: any }
    >({
      query: ({ opporId, data }) => {
        // Use PATCH and send payload as 'data' (not 'body') for axiosBaseQuery
        return {
          url: `/opportunity-pipeline/${opporId}`,
          method: "put",
          data, // axiosBaseQuery expects 'data' for payload
        };
      },
      invalidatesTags: ["STATUS"],
      async onQueryStarted(
        { opporId: _opporId, data: _data },
        { dispatch, queryFulfilled },
      ) {
        try {
          await queryFulfilled;
          // Force refetch of the main opportunities list after successful update
          dispatch(creditCommittee.util.invalidateTags(["STATUS"]));
        } catch (error) {
          // Handle error if needed
        }
      },
    }),

    GetOppoPrDetail: builder.query<any, string>({
      query: (mapName) => ({
        url: `/opportunity-pipeline/name/${mapName}`,
        method: "get",
      }),
      transformResponse: (response) => response?.data,
      providesTags: ["STATUS"],
    }),

    getOpportunityPicklist: builder.query<OpportunityPicklistData, null>({
      query: () => ({ url: "/opportunity-pipeline/picklist", method: "get" }),
      transformResponse: (response) => response?.data,
      providesTags: ["STATUS"],
    }),

    updateAccountCallStatus: builder.mutation({
      query: ({ accountId, data }) => ({
        url: `/accounts/${accountId}/call-status`,
        method: "PATCH",
        data: {
          // map_name: mapName,
          ...data,
        },
      }),
      invalidatesTags: ["STATUS"],
    }),
  }),
});

export const {
  useGetCCOnboardingStatusesQuery,
  useGetOpportunityPicklistQuery,
  useGetOppoPrDetailQuery,
  useUpdateOppoPrDetailMutation,
  useUpdateAccountCallStatusMutation,
} = creditCommittee;
